// src/pages/Company.tsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import InquireComponent from "../components/Inquire.tsx";
import HomepageEnquiry from "../components/HomepageEnquiry.tsx";
import MobileHomepageEnquiry from "../components/MobileHomepageEnquiry.tsx";
import MobileTech from "../components/MobileTech.tsx";

const Container = styled.div`
  width: auto;
  height: auto;
  overflow-x: hidden;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;

  @media (max-width: 1550px) {
    width: auto;
  }
`;

const CustomContainer = styled.div<{
  padding?: string;
  marginTop?: string;
  width?: string;
  height?: string;
  backgroundColor?: string;
  borderRadius?: string;
  display?: string;
  alignItems?: string;
  justifyContent?: string;
  flexDirection?: string;
  mobileFlexDirection?: string;
  gap?: string;
  border?: string;

  // 반응형 옵션
  mobilePadding?: string;
  mobileMargin?: string;
  mobileWidth?: string;
  mobileHeight?: string;
  mobileBackgroundColor?: string;
  mobileBorderRadius?: string;
  mobileGap?: string;
}>`
  display: ${({ display = "flex" }) => display};
  padding: ${({ padding = "0px 0px 0px 0px" }) => padding};
  margin-top: ${({ marginTop = "0" }) => marginTop};
  width: ${({ width = "100%" }) => width};
  height: ${({ height = "auto" }) => height};
  background-color: ${({ backgroundColor = "transparent" }) => backgroundColor};
  border-radius: ${({ borderRadius = "0" }) => borderRadius};
  border: ${({ border = "none" }) => border};
  align-items: ${({ alignItems = "center" }) => alignItems};
  justify-content: ${({ justifyContent = "center" }) => justifyContent};
  flex-direction: ${({ flexDirection = "row" }) => flexDirection};
  gap: ${({ gap = "0" }) => gap};
  box-sizing: border-box;

  @media (max-width: 1550px) {
    padding: ${({ mobilePadding }) => mobilePadding || "inherit"};
    margin: ${({ mobileMargin }) => mobileMargin || "inherit"};
    width: ${({ mobileWidth }) => mobileWidth || "inherit"};
    height: ${({ mobileHeight }) => mobileHeight || "inherit"};
    background-color: ${({ mobileBackgroundColor }) =>
      mobileBackgroundColor || "inherit"};
    border-radius: ${({ mobileBorderRadius }) =>
      mobileBorderRadius || "inherit"};
    flex-direction: ${({ mobileFlexDirection }) =>
      mobileFlexDirection || "inherit"};
    gap: ${({ mobileGap }) => mobileGap || "inherit"};
  }
`;

const Text = styled.div<{
  marginTop?: string;
  color?: string;
  fontWeight?: string | number;
  fontSize?: string;
  textAlign?: string;

  mobileMarginTop?: string; // 모바일 전용 margin-top
  mobileFontSize?: string; // 모바일 전용 font-size
}>`
  display: flex;
  margin-top: ${({ marginTop = "0" }) => marginTop};
  color: ${({ color = "white" }) => color};
  font-weight: ${({ fontWeight = "bold" }) => fontWeight};
  font-size: ${({ fontSize = "32px" }) => fontSize};
  font-family: "Pretendard";
  text-align: ${({ textAlign = "center" }) => textAlign};
  white-space: pre-wrap;
  word-break: break-word;

  @media (max-width: 1550px) {
    font-size: ${({ mobileFontSize }) => mobileFontSize || "inherit"};
    margin-top: ${({ mobileMarginTop = "30px" }) => mobileMarginTop};
  }
`;

const Icon = styled.img<{
  width: string;
  height: string;
  marginBottom?: string;
  borderRadius?: string;
  border?: string;
}>`
  width: ${(props) => props.width}; // width 설정
  height: ${(props) => props.height}; // height 설정
  margin-bottom: ${(props) => props.marginBottom}; // height 설정
  border-radius: ${(props) => props.borderRadius}; // border-radius 설정
  border: ${(props) => props.border}; // border 설정
  @media (max-width: 1550px) {
    max-width: 40px;
    mask-width: 40px;
    margin-bottom: 5px;
  }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 300px;
  height: 478px;
  border-radius: 10px;
  background-color: #ffffff19;
  gap: 15px;
  margin-top: 20px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  height: 45px;
`;

const Number = styled.div`
  display: flex;
  font-weight: bold;
  color: #2051ad;
  font-size: 20px;
  width: 30px;
`;

const Bar = styled.div`
  width: 2px;
  height: 20px;
  background-color: #ffffff19; // 바의 색상 설정
  margin-right: 10px;
`;

const LongBar = styled.div`
  width: 2px;
  height: 600px;
  background-color: #ffffff19; // 바의 색상 설정
  margin-right: 10px;
`;

const FeatureBox = ({
  items,
}: {
  items: { number: string; text: string }[];
}) => {
  return (
    <Box>
      {items.map((item) => (
        <Item key={item.number}>
          <Number>{item.number}</Number>
          <Bar />
          <Text fontSize="20px" mobileFontSize="20px" mobileMarginTop="0px">
            {item.text}
          </Text>
        </Item>
      ))}
    </Box>
  );
};

const FormContainer = styled.div`
  background-color: #000;
  padding: 40px;
  border-radius: 10px;
  width: 100%;
  max-width: 800px;
  margin: auto;
  color: #fff;
`;

const Row = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  display: flex;
  flex: 1;
  padding: 12px;
  font-size: 16px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid #2051ad;
  background-color: #ffffff;
  outline: none;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  height: 100%;
  transition: background-color 0.2s ease-in-out, border 0.2s ease-in-out;
  &:hover {
    background-color: #c5d9ff;
  }
  &:focus {
    border: 1px solid #103d80; /* 클릭 시 더 진한 파란색 */
    background-color: #d9e6ff; /* 클릭 후 입력 중일 때 색 */
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 12px;
  font-size: 16px;
  box-sizing: border-box;
  border-radius: 5px;
  border: 1px solid #ccc;
  background-color: #eef;
`;

const Label = styled.label`
  font-size: 16px;
  font-weight: bold;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const MobileRadioGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr); // 2열로 정렬
  gap: 10px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: #fff;
  @media (max-width: 1550px) {
    font-size: 14px; // 모바일에서는 폰트 크기 줄이기
  }
`;

const Button = styled.button`
  margin-top: 20px;
  display: inline-block; /* 또는 block */
  width: 270px;
  height: 65px;
  padding: 15px;
  font-size: 18px;
  border-radius: 100px;
  border: none;
  background-color: white;
  color: black;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s ease-in-out, transform 0.2s ease-in-out;

  &:hover {
    background-color: #2051ad;
    color: white;
    transform: scale(1.05);
  }
`;

function Portfolio() {
  const isMobile = window.innerWidth <= 1550;

  const marektingInside = [
    { number: "01", text: "SNS 로그인" },
    { number: "02", text: "문자 발송" },
    { number: "03", text: "멀티 팝업" },
    { number: "04", text: "미디어 채널 연동" },
    { number: "05", text: "메인페이지 관리" },
    { number: "06", text: "맞춤형 게시판" },
    { number: "07", text: "메타태그 및 페이지 설정" },
    { number: "08", text: "사진/영상 촬영" },
    { number: "09", text: "AI 모델 이미지 제공" },
  ];
  const additionalOption = [
    { number: "01", text: "다양한 애니메이션 삽입" },
    { number: "02", text: "기능 커스텀(내용 협의)" },
  ];
  return (
    <Container>
      <Text
        marginTop="100px"
        fontSize="32px"
        mobileFontSize="27px"
        mobileMarginTop="50px"
      >
        신환을 일으키는 홈페이지 {isMobile && <br />}슬라임에서 제작해드립니다.
      </Text>
      <Text
        marginTop="20px"
        fontSize="18px"
        whiteSpace="pre-wrap"
        fontWeight="normal"
        mobileFontSize="16px"
      >
        최신 트렌드에 맞는 디자인, 원하는 기능을 커스텀하고,{"\n"} 추후
        유지보수와 관리부분에서 편리한 홈페이지를 {isMobile && <br />}
        제작해드립니다.
      </Text>

      <CustomContainer
        marginTop="50px"
        width="950px"
        height="573px"
        flexDirection="row"
        gap="50px"
        mobileFlexDirection="column"
        mobileGap="10px"
      >
        <CustomContainer
          flexDirection="column"
          width="369px"
          height="600px"
          marginTop="20px"
        >
          <Text fontSize="32px" marginTop="10px" mobileFontSize="27px">
            국내 마케팅
          </Text>
          <FeatureBox items={marektingInside} />
        </CustomContainer>
        {isMobile ? null : <LongBar />}
        <CustomContainer
          flexDirection="column"
          width="369px"
          height="600px"
          marginTop="20px"
        >
          <Text fontSize="32px" marginTop="10px" mobileFontSize="27px">
            추가 옵션
          </Text>
          <FeatureBox items={additionalOption} />
        </CustomContainer>
      </CustomContainer>

      <CustomContainer marginTop="120px" flexDirection="column" gap="20px">
        <Text fontSize="32px" mobileFontSize="27px">
          홈페이지 제작 견적문의
        </Text>
        {isMobile ? (
          <MobileHomepageEnquiry></MobileHomepageEnquiry>
        ) : (
          <HomepageEnquiry></HomepageEnquiry>
        )}
      </CustomContainer>

      <InquireComponent isMobile={false} />
    </Container>
  );
}

export default Portfolio;
